import {
  InfiniteScrollGrid,
  useBaseStore
} from '@roc/feature-app-core';
import {
  GridColumnProps,
  LinkColumn, SelectFilterComponent,
  SelectFloatingFilterComponent, StatusChip,
  StatusType, TextColumn,
  tooltipHeaderTemplate, FastForwardIcon, SubmitMenuItem
} from '@roc/ui';
import { formatCurrency } from '@roc/ui/utils';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useLoanRoutes, usePlatformLoanRoutes } from '@roc/feature-loans-routes-config';
import { useLoanStore } from '../hooks/useLoanStore';
import { useManualQuoteStore } from '../hooks/useManualQuoteStore';
import { tooltips } from '../utils/tooltips';
import { getUnreadMessagesCount, StyledBadge } from './myPipelineLoansGrid';
import { useUserStore } from '@roc/feature-app-core';
import { IconButton, ListItemIcon, ListItemText, Menu } from '@material-ui/core';
import { ImportExport, MoreVert } from '@material-ui/icons';
import { ChangeLoanStatusForm } from './changeLoanStatusForm';
import { ManualQuoteDeclineDialog } from 'libs/feature-loan-details/src/loanDetails/components/bridge/manualQuoteDeclineDialog';
import {
  isActiveManualQuote,
  capitalProviderOptions,
  CapitalProvider,
  LoanStatus,
} from '@roc/feature-utils';

const isPreSubmissionStatus = (status?: string) =>
  status === LoanStatus.PRE_SUBMISSION ||
  status === LoanStatus.PENDING_SUBMISSION;

const usdAmount: any = params => {
  return params.value && formatCurrency(Number(params.value));
};

const noOpComparator = () => 0;

const REGULAR_STATUS_OPTIONS = [
  { label: 'Declined', value: 'DECLINED' },
  { label: 'Funded', value: 'FUNDED' },
  { label: 'Lost', value: 'LOST' },
  { label: 'On Hold', value: 'ON_HOLD' },
  { label: 'Paid Off', value: 'PAID_OFF' },
  { label: 'Paid Off Buyout', value: 'PAID_OFF_BUYOUT' },
  { label: 'Retracted', value: 'RETRACTED' },
  { label: 'Unknown', value: 'UNKNOWN' },
].sort((a, b) => a.label.localeCompare(b.label));

const columns: GridColumnProps[] = [
  {
    field: 'dealid',
    headerName: 'Loan Id',
    minWidth: 100,
    cellRenderer: 'loanIdCellRenderer',
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    pinned: 'left',
    lockPinned: true,
  },
  {
    field: 'dealName',
    headerName: 'Deal Name',
    minWidth: 275,
    cellRenderer: 'dealNameCellRenderer',
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 120,
    cellRenderer: 'statusCellRenderer',
    comparator: noOpComparator,
    filter: 'selectFilter',
    floatingFilter: true,
    floatingFilterComponent: 'selectFloatingFilter',
    floatingFilterComponentParams: {
      suppressFilterButton: true,
      options: REGULAR_STATUS_OPTIONS,
    },
  },
  {
    field: 'loanSubType',
    headerName: 'Loan Type',
    minWidth: 200,
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'amount',
    headerName: 'Amount',
    minWidth: 130,
    valueFormatter: usdAmount,
    comparator: noOpComparator,
  },
  {
    field: 'fastrackPaymentComplete',
    headerName: 'Fast Tracked Loan',
    minWidth: 125,
    cellRenderer: 'fastTrackCellRenderer',
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'submissionDate',
    headerName: 'Submission Date',
    minWidth: 185,
    comparator: noOpComparator,
    filter: 'agDateColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'closingDate',
    headerName: 'Closing Date',
    minWidth: 185,
    comparator: noOpComparator,
    filter: 'agDateColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'borrowerEntity',
    headerName: 'Borrower Entity',
    minWidth: 135,
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'interestEconomics',
    headerName: 'Interest Economics',
    minWidth: 200,
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    headerTooltip: tooltips.interestEconomics,
    headerComponentParams: {
      template: tooltipHeaderTemplate,
    },
  },
  {
    field: 'keyRatios',
    headerName: 'Key Ratios',
    minWidth: 200,
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    headerTooltip: tooltips.keyRatios,
    headerComponentParams: {
      template: tooltipHeaderTemplate,
    },
  },
  {
    field: 'payoffDate',
    headerName: 'Payoff Date',
    minWidth: 125,
    comparator: noOpComparator,
    filter: 'agDateColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'submittedBy',
    headerName: 'Submitted By',
    minWidth: 300,
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'coordinator',
    headerName: 'Processor',
    minWidth: 300,
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'underwriter',
    headerName: 'Underwriter',
    minWidth: 300,
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'lenderOriginatorName',
    headerName: 'Originator',
    minWidth: 300,
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'manualQuoteDescription',
    headerName: 'Manual Quote',
    minWidth: 125,
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    }
  },
  {
    headerName: 'Action',
    minWidth: 80,
    cellRenderer: 'actionCellRenderer',
    comparator: noOpComparator,
    width: 80,
    pinned: 'right',
    lockPinned: true,
    field: 'dealid'
  },
];

const addCapitalProviderColumn = columns => {
  const capitalProviderColumn = {
    field: 'capitalProvider',
    headerName: 'Capital Provider',
    minWidth: 150,
    sortable: false,
    filter: 'selectFilter',
    floatingFilter: true,
    floatingFilterComponent: 'selectFloatingFilter',
    floatingFilterComponentParams: {
      suppressFilterButton: true,
      options: capitalProviderOptions,
    },
  };
  const [loanIdColumn, ...otherColumns] = columns;
  return [loanIdColumn, capitalProviderColumn, ...otherColumns];
};

const UnreadMessagesBadge = observer(({ rowData, loanId, store, children }) => {
  const loan = store.gridData.data.rows.find(row => row.dealid === loanId);
  return (
    <StyledBadge
      badgeContent={getUnreadMessagesCount(
        loan ? loan?.unreadMessages : rowData?.unreadMessages
      )}
    >
      {children}
    </StyledBadge>
  )
});

export const MyLoansGrid = observer(({ isForBackUpLoans = false }) => {
  const { globalStore } = useBaseStore();
  const { loanStore } = useLoanStore();
  const { manualQuoteStore } = useManualQuoteStore();
  const { loanRoutesConfig } = useLoanRoutes();
  const { platformLoanRoutesConfig } = usePlatformLoanRoutes();
  const { userStore } = useUserStore();
  const [anchorEl, setAnchorEl] = useState<HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [loanId, setLoanId] = useState(0);
  const [rowStatus, setRowStatus] = useState<string | null>(null);
  const [showDeclineManualQuote, setShowDeclineManualQuote] = useState(false);
  const allowLoanPricing = userStore.allowLoanPricing;
  const lenderId = globalStore.lenderInfo?.lenderId;
  const usePipelineChangeStatusOptions = isPreSubmissionStatus(rowStatus);

  const gridStore = globalStore.showUnifiedLoans
    ? loanStore.unifiedMyLoansGridStore
    : loanStore.myLoansGridStore;
  const activeGridStore = isForBackUpLoans
    ? loanStore.myBackUpLoansGridStore
    : gridStore;

  const customStatuses = loanStore.unifiedMyLoansGridStore.customStatuses;

  useEffect(() => {
    if (globalStore.showUnifiedLoans && lenderId) {
      loanStore.unifiedMyLoansGridStore.fetchCustomStatuses(lenderId);
    }
  }, [globalStore.showUnifiedLoans, lenderId]);

  const _columns = useMemo(() => {
    let cols = columns;
    if (globalStore.showUnifiedLoans) {
      cols = addCapitalProviderColumn(cols);
      if (lenderId) {
        cols = cols.map(col => {
          if (!('field' in col) || col.field !== 'status') return col;
          return {
            ...col,
            floatingFilterComponentParams: {
              suppressFilterButton: true,
              getOptions: () => [
                ...REGULAR_STATUS_OPTIONS,
                ...loanStore.unifiedMyLoansGridStore.customStatuses.map(cs => ({
                  label: cs.name,
                  value: `CUSTOM_STATUS:${cs.name}`,
                })),
              ].sort((a, b) => a.label.localeCompare(b.label)),
            },
          };
        });
      }
    }
    return cols;
  }, [globalStore.showUnifiedLoans, lenderId]);

  const isExternalCapital = row => {
    const externalCapitalLabel = capitalProviderOptions.find(
      opt => opt.value === CapitalProvider.EXTERNAL_CAPITAL
    ).label;
    return row?.capitalProvider === externalCapitalLabel;
  };

  const frameworkComponents = {
    selectFilter: SelectFilterComponent,
    selectFloatingFilter: SelectFloatingFilterComponent,
    loanIdCellRenderer: params => {
      const rocUrl = loanRoutesConfig.loans(params.value).children.dashboard
        .url;
      const value = params.value;
      const externalCapitalUrl = platformLoanRoutesConfig.loans(params.value).children.dashboard.url;
      return loanStore.isLoanSubtypeSupported(params.node.data?.loanSubType) ? (
        <UnreadMessagesBadge
          loanId={params.node.data?.dealid}
          rowData={params.node.data}
          store={
            isForBackUpLoans ? loanStore.myBackUpLoansGridStore : gridStore
          }
        >
          {LinkColumn({
            ...params,
            value,
            url: isExternalCapital(params.node.data) ? externalCapitalUrl : rocUrl,
          })}
        </UnreadMessagesBadge>
      ) : (
        TextColumn({ ...params, value })
      );
    },
    dealNameCellRenderer: params => {
      const loanId = params.node.data?.dealid;
      const externalCapitalUrl = platformLoanRoutesConfig.loans(loanId).children.dashboard.url;
      return LinkColumn({
        ...params,
        url: isExternalCapital(params.node.data)
          ? externalCapitalUrl
          : loanRoutesConfig.loans(loanId).children.dashboard.url,
      });
    },
    fastTrackCellRenderer: ({ value }) => {
      if (!value) {
        return null;
      }
      return (<FastForwardIcon value={value} />)
    },
    actionCellRenderer: params => {
      const status = params.node.data?.status;
      const hasActiveManualQuote = isActiveManualQuote(
        params.node.data?.manualQuoteStatus
      );
      const showAction =
        hasActiveManualQuote ||
        loanStore.isLoanStatusRetractedOrOnHold(status) ||
        isPreSubmissionStatus(status);
      if (showAction) {
        return (
          <IconButton onClick={e => openMenu(e, params.node.data)}>
            <MoreVert color="primary" />
          </IconButton>
        );
      }
      return null;
    },
    statusCellRenderer: (params) =>
      params.value ? (
        <StatusChip statusType={StatusType.LOAN} label={params.value} size="small" displayName={params.data?.statusName || undefined} />
      ) : null,
  };
  const checkLoanPricingFlag = (columns) => {
    if (!allowLoanPricing) {
      return columns.filter(c => c.field != "interestEconomics" && c.field != "keyRatios");
    } else {
      return columns;
    }
  }


  const openMenu = (event: React.MouseEvent<HTMLElement>, record) => {
    const { dealid, status } = record;
    const hasActiveManualQuote = isActiveManualQuote(record?.manualQuoteStatus);

    setLoanId(dealid);
    setRowStatus(status ?? null);
    setShowDeclineManualQuote(hasActiveManualQuote);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setRowStatus(null);
    setShowDeclineManualQuote(false);
  };

  return (
    <>
      <InfiniteScrollGrid
        columns={checkLoanPricingFlag(_columns)}
        frameworkComponents={frameworkComponents}
        store={activeGridStore}
      />
      <Menu
        id="menu-appbar"
        getContentAnchorEl={null}
        anchorEl={anchorEl}
        keepMounted
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        open={menuOpen}
        onClose={handleMenuClose}
      >
        {!showDeclineManualQuote && (
          <SubmitMenuItem
            testId={`change-loan-status-${loanId}`}
            onClick={() => {
              loanStore.submitFormStatus(loanId, 'myLoans');
              handleMenuClose();
            }}
            submitDialogProps={{
              title: `Change Status for Loan ${loanId}`,
              body: (
                <ChangeLoanStatusForm
                  fromMyLoans={!usePipelineChangeStatusOptions}
                  loanId={loanId}
                />
              ),
              okButtonText: 'Submit',
              submitValidation: () => {
                loanStore.loanStatusStore.runFormValidation();
                return loanStore.loanStatusStore.form.meta.isValid;
              },
            }}
          >
            <ListItemIcon>
              <ImportExport fontSize="small" />
            </ListItemIcon>
            <ListItemText>Change Status</ListItemText>
          </SubmitMenuItem>
        )}
        {showDeclineManualQuote && (
          <SubmitMenuItem
            testId={`decline-manual-quote-${loanId}`}
            onClick={async () => {
              const data = {
                loanId,
                source: 'cportal',
              };
              await manualQuoteStore.declineManualQuote(data);
              activeGridStore.resetAndFetchGridData();
              handleMenuClose();
            }}
            submitDialogProps={{
              title: `Decline Manual Quote ${loanId}`,
              body: <ManualQuoteDeclineDialog />,
              okButtonText: 'Submit',
            }}
          >
            <ListItemIcon>
              <ImportExport fontSize="small" />
            </ListItemIcon>
            <ListItemText>Decline Manual Quote</ListItemText>
          </SubmitMenuItem>
        )}
      </Menu>
    </>
  );
});
